import { PageHeader, MultiSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { useTran } from "@/hooks";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  ClipboardList,
  List,
  TableProperties,
  UploadCloud,
  Zap,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  CategoryTabs,
  DisclosureSidebar,
  DisclosureHeader,
  ComputedEntryView,
  EntryViewSkeleton,
  PrefillBanner,
  PrefillModal,
  StandardEntryView,
  SubEntryView,
} from "../components";
import {
  useAssignmentStore,
  useDisclosureStore,
  useEntryUIStore,
} from "../hooks/useDataEntryStore";
import { useDelayedFlag } from "@/hooks/useDelayedFlag";
import { useAuthStore } from "@/features/auth/hooks";
import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  loadDisclosures,
  loadDataEntries,
  loadDisclosurePresence,
  loadOrgList,
} from "../operations/load.ops";
import { loadAssignments } from "../operations/assignment.ops";
import { loadPrefillSuggestions } from "../operations/prefill.ops";
import DataEntryNavTabs from "../components/DataEntryNavTabs";
import BatchUploadDialog from "../components/batch-upload/BatchUploadDialog";
import NxmapBulkSyncDialog from "../components/dialogs/NxmapBulkSyncDialog";
import { Can } from "@/hooks/useCan/Can";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";
import { loadReportingYears } from "@/features/reporting-boundary/operations/reporting-boundary.ops";
import { getFrameworkMeta } from "@/lib/constants/frameworks.metadata";
import { useState } from "react";

const DataEntryPage = observer(() => {
  const { t } = useTran();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const { user, userRole, isDelegationOnlyUser, isJJSEA } = useAuthStore();
  const permissions = user?.permissions || [];
  const {
    selectedFrameworks,
    //frameworks,
    setSelectedFrameworks,
    activeDisclosure,
    disclosures,
    setActiveDisclosure,
    setActiveCategory,
    setPresenceFilter,
    disclosuresMatchingNonCategoryFilters,
  } = useDisclosureStore();
  const { setFilterAssignedToMe } = useAssignmentStore();
  const {
    loadingEntries,
    setBatchUploadOpen,
    setShowNxmapBulkSyncDialog,
    setMobileSidebarOpen,
  } = useEntryUIStore();
  const showEntrySkeleton = useDelayedFlag(loadingEntries);
  const urlAppliedRef = useRef(false);

  const [reportingYears, setReportingYears] = useState([]);
  const [availableFrameworks, setAvailableFrameworks] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const reportingYears = await loadReportingYears();
      setReportingYears(reportingYears);

      setAvailableFrameworks(reportingYearStore.availableFrameworks);
    };

    fetchData();
  }, []);

  const frameworks = useMemo(() => {
    const frameworkNameByCode = new Map(
      (availableFrameworks || [])
        .map((framework) => {
          const code =
            framework?.code ||
            framework?.framework ||
            framework?.value ||
            framework?.id ||
            framework?._id;

          if (!code) return null;

          return [
            String(code).trim(),
            String(framework?.name || framework?.label || code).trim(),
          ];
        })
        .filter(Boolean),
    );

    return Array.from(
      new Set(
        (reportingYears || []).flatMap((reportingYear) =>
          Array.isArray(reportingYear?.frameworkCodes)
            ? reportingYear.frameworkCodes
            : [],
        ),
      ),
    )
      .map((code) => String(code || "").trim())
      .filter(Boolean)
      .map((code) => ({
        code,
        name: frameworkNameByCode.get(code) || getFrameworkMeta(code).label,
      }));
  }, [availableFrameworks, reportingYears]);

  useEffect(() => {
    loadDisclosures({ initFrameworks: true });
    loadAssignments();
    loadDisclosurePresence();
  }, []);

  // Auto-select from URL query params (from ESG dashboard links)
  useEffect(() => {
    if (urlAppliedRef.current || !disclosures.length) return;

    const metricId = searchParams.get("metricId");
    const filled = searchParams.get("filled");

    if (!metricId) return;

    const disclosure = disclosures.find((d) => d._id === metricId);
    if (disclosure) {
      if (disclosure.category) {
        setActiveCategory(disclosure.category);
      }
      setActiveDisclosure(disclosure);
    }

    if (filled === "true") {
      setPresenceFilter("filled");
    } else if (filled === "false") {
      setPresenceFilter("empty");
    }

    urlAppliedRef.current = true;
  }, [disclosures]);

  useEffect(() => {
    loadOrgList();
    if (isDelegationOnlyUser) {
      setFilterAssignedToMe(true);
    }
  }, []);

  useEffect(() => {
    if (activeDisclosure?._id) {
      loadDataEntries();
      loadPrefillSuggestions();
    }
  }, [activeDisclosure?._id]);

  useEffect(() => {
    if (!isJJSEA) return;

    // Only initialize if nothing is selected
    if (selectedFrameworks.length > 0) return;

    const jjGroup = frameworks.find(
      (framework) => framework.code === "JJ-Group",
    );

    if (jjGroup) {
      setSelectedFrameworks([jjGroup.code]);
    }
  }, [isJJSEA, frameworks, selectedFrameworks]);

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-y-4">
      <PageHeader
        title={t("Data Entry")}
        description={t(
          "Record and manage your ESG disclosure data across all frameworks",
        )}
        icon={TableProperties}
        actions={<DataEntryNavTabs />}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {isMobile && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <List size={14} />
              {t("Disclosures")}
            </Button>
          )}
          <MultiSelect
            options={frameworks.map((fw) => ({
              value: fw.code,
              label: fw.name,
            }))}
            value={selectedFrameworks}
            onChange={setSelectedFrameworks}
            placeholder={t("All Frameworks")}
            searchPlaceholder={t("Search frameworks...")}
            className="w-50"
          />
          <CategoryTabs />
        </div>

        <div className="flex items-center gap-2">
          {user?.nxmapConnected && userRole === "super_admin" && (
            <Can
              resource="esg_data_entry"
              action="create"
              userPermissions={permissions}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNxmapBulkSyncDialog(true)}
              >
                <Zap size={14} />
                {t("NXMap Bulk Sync")}
              </Button>
            </Can>
          )}
          <Can
            resource="esg_data_entry"
            action="view"
            userPermissions={permissions}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBatchUploadOpen(true)}
            >
              <UploadCloud size={14} />
              {t("Batch Upload")}
            </Button>
          </Can>
          <div
            className={`flex items-center px-2.5 h-8 rounded-full text-xs gap-2 transition-all duration-300 border border-sky-500 text-sky-500`}
          >
            <span>{t("Unique Assigned Metrics")}</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full bg-sky-500 text-white`}
            >
              {disclosuresMatchingNonCategoryFilters.length}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col gap-4 lg:flex-row lg:items-stretch border-t border-border">
        <DisclosureSidebar />

        {activeDisclosure ? (
          showEntrySkeleton ? (
            <EntryViewSkeleton />
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-4">
              <div className="flex-1 overflow-y-auto px-1">
                <DisclosureHeader />
                <PrefillBanner />
                {activeDisclosure.isCalculated &&
                activeDisclosure.inputType !== "subEntries" ? (
                  <ComputedEntryView reportingYears={reportingYears} />
                ) : activeDisclosure.inputType === "subEntries" ? (
                  <SubEntryView reportingYears={reportingYears} />
                ) : (
                  <StandardEntryView reportingYears={reportingYears} />
                )}
              </div>
            </div>
          )
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
            <ClipboardList size={48} className="text-muted-foreground/40" />
            <p className="mt-3 text-sm font-medium text-muted-foreground">
              {t("Select a disclosure to view entries")}
            </p>
          </div>
        )}
      </div>

      <PrefillModal />
      <BatchUploadDialog />
      <NxmapBulkSyncDialog />
    </div>
  );
});

export default DataEntryPage;
